import { Box, Card, CardContent, Typography, Divider, Alert, AlertTitle } from '@mui/material';
import { DependencyChangeAlertAPI } from '@analyzer/client';
import SummarizedLink from '../url.js';
import { getSeverityColor } from './severity.js';
import { DomainUtils } from '@analyzer/utilities/lib/domain.js';
import { getAlertTitle } from './title.js';

interface DependencyChangeAlertProps {
  alert: DependencyChangeAlertAPI;
}

interface OriginGroup {
  [domain: string]: {
    count: number;
    urls: string[];
  };
}

function groupByDomainWithDiff(current: (string | { request_url: string })[]): OriginGroup {
  const groups: OriginGroup = {};

  current.forEach((item) => {
    const url = typeof item === 'string' ? item : item.request_url;
    let domain: string;
    try {
      domain = DomainUtils.getDomain(url);
    } catch {
      return; // Skip invalid URLs
    }

    if (!groups[domain]) {
      groups[domain] = {
        count: 0,
        urls: []
      };
    }

    groups[domain].count += 1;
    groups[domain].urls.push(url);
  });

  return groups;
}

function renderGroupedOrigins(grouped: OriginGroup) {
  const sortedGroups = Object.entries(grouped).sort(([, a], [, b]) => b.count - a.count);

  return (
    <Box component="ul" sx={{ pl: 2 }}>
      {sortedGroups.map(([domain, { count, urls }]) => (
        <li key={domain}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {domain} ({count} request{count > 1 ? 's' : ''})
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            {urls.map((url, idx) => (
              <li key={idx}>
                <Typography variant="body2">
                  <SummarizedLink url={url} />
                </Typography>
              </li>
            ))}
          </Box>
        </li>
      ))}
    </Box>
  );
}

export function DependencyChangeAlert({ alert }: DependencyChangeAlertProps) {
  const { severity, origins } = alert;
  const groupedOrigins = groupByDomainWithDiff(origins);

  return (
    <Box>
      <Alert severity={getSeverityColor(severity)} sx={{ mb: 2 }}>
        <AlertTitle>{getAlertTitle(alert)}</AlertTitle>
        <Typography variant="body2">
          This website has begun loading resources from new external origins that were not previously
          observed. If this change is unexpected, it may indicate a third-party dependency change or a
          potential risk. Review the details below.
        </Typography>
      </Alert>
      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            New External Origins
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {renderGroupedOrigins(groupedOrigins)}
        </CardContent>
      </Card>
    </Box>
  );
}
